import type { Subtree, ApiKey, NewApiKey, CustomerActionTypes, SelectedOrganisations, OrganisationUser, OrganisationVehicle, ToolConfiguration } from '../types/customer'
import { SUBTREES_FETCHED, FETCHED_API_KEYS, RECEIVED_NEW_API_KEY, EXPIRED_API_KEY_BY_FORCE, FETCHED_ORGANISATION_NAME, SET_SELECTED_ORGANISATIONS, AUTHENTICATED_USER_FETCHED, CHILD_NODE_ADDED, CHILD_NODE_REMOVED, USER_TO_CHILD_NODE_ADDED, ORGANISATION_NODE_SELECTED, FETCH_ORGANISATION_NODE_USERS, UPDATED_PRIVILEGE_TO_USER, VEHICLE_TO_CHILD_NODE_ADDED, USER_FROM_CHILD_NODE_REMOVED, VEHICLE_TO_CHILD_NODE_REMOVED, USER_FROM_CHILD_NODE_MOVED, CHILD_NODE_EDITED, MOVE_USER_TO_CHILD_NODE_FAILED, CLEAR_ALL_ERRORS, CLEAR_FAILED_USER, FETCHED_USERS_FROM_ORGANISATION, FETCHED_VEHICLES_FROM_ORGANISATION, MOVE_USER_TO_CHILD_NODE, FETCHED_ORGANISATION_TOOL_CONFIG } from '../types/customer'

// SUBTREES

function removeNodeFromTree(items: any, targetId: string) {
  if (!items) return null

  return items
    .map((item: any) => {
      if (item.children && item.children.length > 0) {
        item.children = removeNodeFromTree(item.children, targetId)
      }
      return item
    })
    .filter((item: any) => item.uuid !== targetId) 
}

function renameNodeInTree(items: any, targetId: string, newName: string) {
  if (!items) return null

  return items.map((item: any) => {
    if (item.children && item.children.length > 0) {
      item.children = renameNodeInTree(item.children, targetId, newName)
    }
    if (item.uuid === targetId) {
      return { ...item, name: newName }
    }
    return item
  })
}


export interface Subtrees {
  subtrees: Subtree[]
  total: number,
  totalChildren: number
}
export interface UserError {
  email: string;
  message: string;
  name: string;
}

export interface OrganisationNode {
  uuid: string | undefined,
  users: OrganisationUsers[]
  vehicles: OrganisationVehicles[]; 
  userErrors: UserError[]
}
export interface OrganisationUsers {
  id: string | null,
  firstName: string,
  lastName: string,
  email: string,
  privilege: string 
}
export interface OrganisationVehicles {
  id: string,
  vehicleName: string,
  model: string,
  year: string,
  licenseNumber: string,
}


export const VEHICLES_FETCHED_SORTED = 'VEHICLES_FETCHED_SORTED'
interface VehicleSortedFetched {
  type: typeof VEHICLES_FETCHED_SORTED,
  data: VehicleSortedFetchedData,
}

interface VehicleSortedFetchedData  {
  boxes: FetchedVehicles[],
  total: number
}

export interface FetchedVehicles {
  mainbox_id: string;
  vehicleName: string;
  vehicle: {
    licenseNumber: string,
    modelName: string,
    brandName: string,
    year: string,
  }
  organisationTags: string[],
}


export interface AllUsers {
  users: OrganisationUser[],
  total: number
}
export interface AllVehicles {
  vehicles: OrganisationVehicle[],
  total: number
}

const initialSubtrees: Subtrees = {
  subtrees: [],
  total: 0,
  totalChildren: 0
}
export const subtrees = (state = initialSubtrees, action: CustomerActionTypes) : Subtrees => {
  switch (action.type) {
    case SUBTREES_FETCHED:

      let totalChildrenFetched = 0
      action.subtrees.map(ch => {
        totalChildrenFetched += ch.children?.length || 0
      })
      return {
        subtrees: action.subtrees,
        total: action.total,
        totalChildren: totalChildrenFetched
      }
    case CHILD_NODE_ADDED:
      const trees = state.subtrees
      const parent = trees.find(x => x.uuid === action.parentId)
      if (parent) {
        parent.children = parent.children || []
        parent?.children.push({
          uuid: action.uuid,
          name: action.name,
          services: [],
          children: []
        })
      }
      
      const totalChildrenAdded = state.totalChildren +1
      return {
        ...state,
        subtrees: trees,
        totalChildren: totalChildrenAdded
      }

    case CHILD_NODE_REMOVED:
      const removeTrees = state.subtrees?.map(parent => {
        
        return {
          ...parent,
          children: removeNodeFromTree(parent.children, action.id)
        }
      })
      const totalChildrenRemoved = state.totalChildren -1

      return {
        ...state,
        subtrees: removeTrees,
        totalChildren: totalChildrenRemoved
      }
    
    case CHILD_NODE_EDITED: {
      if (!action.id || !action.newName) return state
      
      return {
        ...state,
        subtrees: renameNodeInTree(state.subtrees, action.id, action.newName)
      }
    }
      

    default:
      return state
    
  }
}

// ORGANISATION

export const organisationNames = (state = {}, action: CustomerActionTypes) => {
  switch (action.type) {
    case FETCHED_ORGANISATION_NAME:
      return {
        ...state,
        [action.organisation.uuid]: action.organisation.translated
      }
    default:
      return state
  }
}

const initialOrganisationNode: OrganisationNode = {
  uuid: undefined,
  users: [],
  vehicles: [],
  userErrors: [], 
}

export const organisation_node = (
  state: OrganisationNode = initialOrganisationNode,
  action: CustomerActionTypes | VehicleSortedFetched
): OrganisationNode => {  
  switch (action.type) {
    case ORGANISATION_NODE_SELECTED:
      return {
        uuid: action.id,
        users: [],
        vehicles: [],
        userErrors: [],

      }
    case FETCH_ORGANISATION_NODE_USERS:
      return {
        ...state,
        uuid: action.id,
        users: action.users,

      }

    case USER_TO_CHILD_NODE_ADDED:
      const addedUsers: OrganisationUsers[] = state.users
      const existingUser = addedUsers.find(u => u.email === action.email)
      if (existingUser) {
        existingUser.privilege = action.privilege ?? 'standard'
      }
      else {
        addedUsers.push({
          id: null,
          firstName: action.firstName,
          lastName: action.lastName,
          email: action.email,
          privilege: action.privilege ?? 'standard'
        })
      }
      return {
        ...state,
        users: addedUsers
      }
    
    case USER_FROM_CHILD_NODE_MOVED: {
      const addedUsers: OrganisationUsers[] = state.users
      const existingUser = addedUsers.find(user => user.email === action.email)
      
      if (existingUser) {
        existingUser.privilege = action.privilege ?? 'standard'
      } else {
        addedUsers.push({
          id: action.email,
          firstName: action.name?.split(' ')[0],
          lastName: action.name?.split(' ')[1],
          email: action.email,
          privilege: action.privilege ?? 'standard',
        })
      }
      
      return {
        ...state,
        users: addedUsers,
      }
      
    }
    case MOVE_USER_TO_CHILD_NODE_FAILED: {
      const { email, error, name, code } = action.payload
      if (!email) return state
    
      const userErrors = [
        ...state.userErrors.filter(u => u.email !== email),
        { name: name, email, message: error || '', code: code },
      ]
    
      return { ...state, userErrors }
    }
    case MOVE_USER_TO_CHILD_NODE: {
      const email = action.email as string
      return {
        ...state,
        userErrors: state.userErrors.filter((u) => u.email !== email),
      }
    }
    case CLEAR_FAILED_USER: {
      const email = action.payload as string
      return {
        ...state,
        userErrors: state.userErrors.filter((u) => u.email !== email),
      }
    }

    case CLEAR_ALL_ERRORS: {
      return { ...state, userErrors: [] }
    }

    case USER_FROM_CHILD_NODE_REMOVED:
      if (state.uuid !== action.organisation)
        return state
      const filteredUsers = state.users.filter((user: OrganisationUsers) => user.email !== action.email)

      return {
        ...state,
        users: filteredUsers
      }

    case UPDATED_PRIVILEGE_TO_USER:
      const updatedUsers = state.users.map((user: OrganisationUsers) =>
        user.email === action.email
          ? { ...user, privilege: action.privilege }
          : user
      )
      return {
        ...state,
        users: updatedUsers
        
      }
    case VEHICLE_TO_CHILD_NODE_ADDED: 
      const addedVehicle: OrganisationVehicles[] = state.vehicles
      const existingVehicle = addedVehicle.find(v => v.id === action.id)

      if(!existingVehicle) {
        addedVehicle.push({
          id: action.id,
          vehicleName: action.vehicleName,
          model: action.model,
          year: action.year,
          licenseNumber: action.licenseNumber,
        })
      }
      return {
        ...state,
        vehicles: addedVehicle
      }
    case VEHICLE_TO_CHILD_NODE_REMOVED:
      const filterVehicles = state.vehicles.filter((vehicle: OrganisationVehicles) => {
        return vehicle.id !== action.id
      })
        
      return {
        ...state,
        vehicles: filterVehicles
      }

    case VEHICLES_FETCHED_SORTED:
        
      const fetchedVehicles: OrganisationVehicles[] = action.data.boxes.map(f => {
        return {
          id: f.mainbox_id,
          vehicleName: f.vehicleName,
          model: `${f.vehicle.brandName} ${f.vehicle.modelName}`,
          year: f.vehicle.year,
          licenseNumber: f.vehicle.licenseNumber
        }
      }) 

      return {
        ...state, 
        vehicles: fetchedVehicles
      }
    default:
      return state
  }
}

export const all_users = (state: AllUsers = {
  users: [], 
  total: 0
}, action: CustomerActionTypes) => {
  switch (action.type) {
    case FETCHED_USERS_FROM_ORGANISATION:
      return {
        users: action.users,
        total: action.total
      }
    default:
      return state
  }
}

export const all_vehicles = (state: AllVehicles = {
  vehicles: [], 
  total: 0
}, action: CustomerActionTypes) => {
  switch (action.type) {
    case FETCHED_VEHICLES_FROM_ORGANISATION:
      return {
        vehicles: action.vehicles,
        total: action.total
      }
    default:
      return state
  }
}
// API KEYS

export interface ApiKeys {
  keys: ApiKey[],
  total: number
}

const initialApiKeys: ApiKeys = {
  keys: [],
  total: 0
}

export const api_keys = (state = initialApiKeys, action: CustomerActionTypes) : ApiKeys => {
  let newKeys: ApiKey[]
  switch (action.type) {
    case FETCHED_API_KEYS:
      return {
        keys: action.keys,
        total: action.total
      }
    case EXPIRED_API_KEY_BY_FORCE:
      newKeys = state.keys.filter(key => key.id !== action.id)
      return {
        keys: newKeys,
        total: newKeys.length
      }
    case RECEIVED_NEW_API_KEY:
      newKeys = [
        ...state.keys,
        {
          id: action.key.public_id,
          expiresAt: action.key.expires_at_utc,
          organisations: []
        }
      ]
      return {
        keys: newKeys,
        total: newKeys.length,
      }
    default:
      return state
  }
}

// NEW API KEY

const initialNewApiKey: NewApiKey = {
  public_id: '',
  expires_at_utc: new Date(),
  api_key: '',
}

export const new_api_key = (state = initialNewApiKey, action: CustomerActionTypes) : NewApiKey => {
  switch (action.type) {
    case RECEIVED_NEW_API_KEY:
      return action.key
    default:
      return state
  }
}

// SELECTED ORGANISATION

export const selected_organisations = (state = [], action: CustomerActionTypes | {type: 'USER_LOGGED_IN'}) : SelectedOrganisations => {
  switch (action.type) {
    case SET_SELECTED_ORGANISATIONS:
      return action.uuids
    case AUTHENTICATED_USER_FETCHED:
      return action.isNewUser ? [] : state
    case 'USER_LOGGED_IN':
      return []
    default:
      return state
  }
}

const initialState: ToolConfiguration = {
  categories: [],
  brands: [],
  equipmentNames: [],
  customCategories: false,
  customBrands: false,
  customEquipmentNames: false
}

export const organisationToolConfig = (state = initialState, action: any) => {
  switch (action.type) {
    case 'FETCH_ORGANISATION_TOOL_CONFIG':
      return null
    case 'FETCHED_ORGANISATION_TOOL_CONFIG':
      return action.toolConfig
    case 'ADD_TOOL_CATEGORIES':
      return { ...state, categories: [...state.categories, ...action.categories] }
    case 'ADD_TOOL_CATEGORY':
      return { ...state, categories: [...state.categories, { name: action.category, iconUrl: action.iconUrl }] }
    case 'ADD_TOOL_BRAND':
      return { ...state, brands: [...state.brands, action.brand] }
    case 'ADD_TOOL_EQUIPMENT_NAME':
      return { ...state, equipmentNames: [...state.equipmentNames, action.equipmentName] }
    case 'REMOVE_TOOL_CATEGORY':
      return { ...state, categories: state.categories.filter(c => c.name !== action.category) }
    case 'REMOVE_TOOL_BRAND':
      return { ...state, brands: state.brands.filter(b => b !== action.brand) }
    case 'REMOVE_TOOL_EQUIPMENT_NAME':
      return { ...state, equipmentNames: state.equipmentNames.filter(e => e !== action.equipmentName) }
    case 'EDIT_TOOL_CATEGORY':
      return { 
        ...state, 
        categories: state.categories.map(c => c.name === action.oldCategory ? { name: action.newCategory, iconUrl: action.newIconUrl } : c) 
      }
    case 'EDIT_TOOL_BRAND':
      return { 
        ...state, 
        brands: state.brands.map(b => b === action.oldBrand ? action.newBrand : b) 
      }
    case 'EDIT_TOOL_EQUIPMENT_NAME':
      return { 
        ...state, 
        equipmentNames: state.equipmentNames.map(e => e === action.oldEquipmentName ? action.newEquipmentName : e) 
      }
    case 'TOGGLE_CUSTOM_CATEGORIES':
      return { ...state, customCategories: action.isEnabled }
    case 'TOGGLE_CUSTOM_BRANDS':
      return { ...state, customBrands: action.isEnabled }
    case 'TOGGLE_CUSTOM_EQUIPMENT_NAMES':
      return { ...state, customEquipmentNames: action.isEnabled }
    case 'ADD_TOOL_CATEGORIES_FAILED':
      return { ...state, categories: state.categories.filter(c => !action.payload.categories.includes(c.name)) }
    case 'ADD_TOOL_CATEGORY_FAILED':
      return { ...state, categories: state.categories.filter(c => c.name !== action.payload.category) }
    case 'ADD_TOOL_BRAND_FAILED':
      return { ...state, brands: state.brands.filter(b => b !== action.payload.brand) }
    case 'ADD_TOOL_EQUIPMENT_NAME_FAILED':
      return { ...state, equipmentNames: state.equipmentNames.filter(e => e !== action.payload.equipmentName) }
    case 'REMOVE_TOOL_CATEGORY_FAILED':
      return { ...state, categories: [...state.categories, { name: action.payload.category, iconUrl: action.payload.iconUrl }] }
    case 'REMOVE_TOOL_BRAND_FAILED':
      return { ...state, brands: [...state.brands, action.payload.brand] }
    case 'REMOVE_TOOL_EQUIPMENT_NAME_FAILED':
      return { ...state, equipmentNames: [...state.equipmentNames, action.payload.equipmentName] }
    case 'EDIT_TOOL_CATEGORY_FAILED':
      return { 
        ...state, 
        categories: state.categories.map(c => c.name === action.payload.newCategory ? { name: action.payload.oldCategory, iconUrl: action.payload.oldIconUrl } : c) 
      }
    case 'EDIT_TOOL_BRAND_FAILED':
      return { 
        ...state, 
        brands: state.brands.map(b => b === action.payload.newBrand ? action.payload.oldBrand : b) 
      }
    case 'EDIT_TOOL_EQUIPMENT_NAME_FAILED':
      return { 
        ...state, 
        equipmentNames: state.equipmentNames.map(e => e === action.payload.newEquipmentName ? action.payload.oldEquipmentName : e) 
      }
    case 'TOGGLE_CUSTOM_CATEGORIES_FAILED':
      return { ...state, customCategories: action.payload.isEnabled }
    case 'TOGGLE_CUSTOM_BRANDS_FAILED':
      return { ...state, customBrands: action.payload.isEnabled }
    case 'TOGGLE_CUSTOM_EQUIPMENT_NAMES_FAILED':
      return { ...state, customEquipmentNames: action.payload.isEnabled }

    default:
      return state
  }
}